'use client'

import React, { useState } from 'react'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import classNames from 'classnames'
import { ProductDigitalData } from '../utils/product-digital-data'
import { DigitalDataOrigin } from '../utils/digital-data-types'
import Spinner from '@dustin-web/microsite-components/src/spinner'
import { ProductLifeCycleState } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import Popover from '@dustin-web/microsite-components/src/popover/popover'
import Button from '@dustin-web/microsite-components/src/button'
import Icon from '@dustin-web/microsite-components/src/icon'
import { getProductLabels } from '../cards/utils'
import { ProductTypeProps } from '../cards'
import { storeCartDetails } from '../utils/cart-local-storage'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import { Product } from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import { CartBaseProduct } from '../hooks/cart/cart-base-product-types'
import ShoppingCartIcon from '@dustin-web/microsite-components/src/icon/generated/shopping-cart-5'
import { UpdateCartWithMultipleProductParams } from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-internal'
import {
  ProductGA4UnbouncedAddToCart,
  useDebouncedAddToCart,
} from '../hooks/cart/use-debounced-add-to-cart'
import useShoppingCart from '../hooks/dataloader/use-shopping-cart'
import { useNotifyAddToCart } from '../hooks/dataloader/use-notify-cart-click'

type AddToCartProps = {
  product: CartBaseProduct | Product
  mainProductErpIdentifier: string | null
  quantity: number
  isAd: boolean
  addToCartText?: string
  ticket: string
  digitalDataOrigin: DigitalDataOrigin | undefined
  digitalDataIndex?: number
  comment?: string | null
  parentCartItemId?: number
  primary?: boolean
  className?: string
  buttonClassName?: string
  showMiniBtn?: boolean
  shoppingCartIcon?: boolean
  preventSetLastUpdatedProductErpIdentifier?: boolean
  hideNotifyMeStatus?: boolean
  [key: string]: string | object | boolean | number | null | undefined
  multipleProducts?: UpdateCartWithMultipleProductParams[]
  soldOutClassNames?: string
  minWidth?: boolean
}

const getDefaultAddToCartText = (
  isPreorder: boolean,
  translate: ReturnType<typeof useTranslations>['translate']
) => {
  if (isPreorder) {
    return translate('PreOrder')
  }
  return translate('AddToCart')
}

const AddToCartButton = ({
  product,
  mainProductErpIdentifier,
  quantity,
  isAd,
  addToCartText,
  ticket,
  digitalDataOrigin,
  digitalDataIndex,
  comment,
  parentCartItemId,
  primary = true,
  className,
  buttonClassName,
  showMiniBtn = false,
  shoppingCartIcon = false,
  preventSetLastUpdatedProductErpIdentifier,
  hideNotifyMeStatus = false,
  multipleProducts,
  minWidth = true,
  soldOutClassNames,
  ...props
}: AddToCartProps) => {
  const { translate } = useTranslations()
  const { cartItems, updateCart, quantityOfProductInCart, updateCartWithMultipleProducts } =
    useShoppingCart()

  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [showMaximumProductsAdded, setMaximumProductsAdded] = useState(false)
  const debouncedAddToCart = useDebouncedAddToCart()
  const notify = useNotifyAddToCart()
  const productInCart = cartItems.find(
    i =>
      i?.product?.productErpIdentifier === product.productErpIdentifier &&
      i.mainProductErpIdentifier === mainProductErpIdentifier &&
      !i.comment &&
      (!parentCartItemId || i.parentCartItemId === parentCartItemId)
  )

  const parentInCart =
    cartItems.find(x => x.id === parentCartItemId) ||
    cartItems.find(
      x =>
        x.productErpIdentifier === mainProductErpIdentifier &&
        x.id === productInCart?.parentCartItemId
    )

  const parentQuantity = parentInCart?.quantity

  quantity =
    !!productInCart?.product?.minQuantityPerOrder &&
    quantity < productInCart.product.minQuantityPerOrder
      ? productInCart.product.minQuantityPerOrder
      : quantity

  let timer: NodeJS.Timeout

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (
      product.availability?.productLifeCycleState === ProductLifeCycleState.Clearance ||
      product.availability?.productLifeCycleState === ProductLifeCycleState.ExternalClearance
    ) {
      const quantityInCart = quantityOfProductInCart(product.productErpIdentifier)
      if (
        product.availability.maxAvailableQuantity &&
        quantityInCart + quantity > product.availability.maxAvailableQuantity
      ) {
        setMaximumProductsAdded(true)
        return
      }
    }
    const alreadyInCartQuantity = productInCart?.quantity ?? 0

    if (multipleProducts?.length) {
      //
      const list = multipleProducts.map(item => {
        const quantityAlreadyInCart =
          cartItems.find(cartItem => cartItem.productErpIdentifier === item.productErpIdentifier)
            ?.quantity ?? 0

        return {
          ...item,
          quantity: quantityAlreadyInCart + item.quantity,
        }
      })

      updateCartWithMultipleProducts([
        ...list,
        {
          product,
          mainProductErpIdentifier: list[0].mainProductErpIdentifier,
          parentCartItemId: parentCartItemId || parentInCart?.id,
          quantity: alreadyInCartQuantity + quantity,
          productErpIdentifier: list[0].mainProductErpIdentifier,
        },
      ])
    } else {
      updateCart(
        product,
        mainProductErpIdentifier,
        alreadyInCartQuantity + quantity,
        comment,
        parentCartItemId || parentInCart?.id,
        preventSetLastUpdatedProductErpIdentifier
      )
    }

    if (isAddingToCart) {
      clearTimeout(timer)
    }

    timer = setTimeout(() => {
      setIsAddingToCart(false)
    }, 500)

    setIsAddingToCart(true)

    const productAlreadyInCart = cartItems.some(
      c => c.productErpIdentifier === product.productErpIdentifier
    )

    const digitalDataCartIndex = productAlreadyInCart ? undefined : cartItems.length + 1

    const label = getProductLabels(product as ProductTypeProps)
    storeCartDetails({ action: 'ADD', product, digitalDataOrigin, label })

    if (multipleProducts?.length) {
      type EventListType = {
        quantity: number
        productListIndex?: number
      } & CartBaseProduct
      const eventList: EventListType[] = multipleProducts.map(({ product, quantity }, key) => ({
        ...product,
        quantity,
        productListIndex: key,
      }))
      eventList.push({
        ...product,
        quantity,
      }) // add main product
      ProductDigitalData.pushAddMultipleProductsCartEvent(
        eventList,
        digitalDataOrigin,
        'buyButton',
        digitalDataCartIndex
      )
    } else {
      const products: ProductGA4UnbouncedAddToCart[] = [
        {
          ...product,
          digitalDataCartIndex: digitalDataIndex,
          productListIndex: digitalDataIndex,
          label,
          quantity,
          digitalDataOrigin: digitalDataOrigin,
        },
      ]
      debouncedAddToCart(products)
    }

    if (ticket) {
      return notify(ticket)
    }
  }

  if (!product.availability?.isAvailableForSale) {
    const classes = soldOutClassNames || 'u-w-100 d-flex align-items-center u-color-pop-red u-pr-32'
    return <div className={classes}>{translate('SoldOut')}</div>
  }

  const isDisabled =
    (!!productInCart?.isDependent &&
      typeof parentQuantity === 'number' &&
      productInCart.quantity >= parentQuantity) ||
    (!!productInCart?.product?.maxQuantityPerOrder &&
      productInCart.product.maxQuantityPerOrder < quantity + productInCart.quantity) ||
    props.disabled === true

  const addToCartButtonText =
    addToCartText || getDefaultAddToCartText(!!product?.availability?.isPreOrder, translate)

  let isNotifyEligible = false
  if ('IsNotifyEligible' in product.availability) {
    isNotifyEligible = product.availability.IsNotifyEligible
  }

  const productUrl = getProductUrl(product.productErpIdentifier, product.nameSlug)

  const handleClick = () => {
    window.location.href = productUrl
  }

  if (isNotifyEligible && !hideNotifyMeStatus) {
    return (
      <Popover
        content={translate('MaxQuantityReached')}
        onToggle={() => setMaximumProductsAdded(false)}
        open={showMaximumProductsAdded}
        className={className}
        theme="light-brown"
        closeBtn={false}
        positions={['top-free']}
        style={{ display: 'grid' }}
        closeOnOutsideClick>
        <Button
          data-test-id="add-to-cart-button"
          aria-label={translate('NotifyMe_Stock')}
          {...props}
          onClick={handleClick}
          className={classNames('c-add-to-cart-btn c-btn--block u-mb-0', buttonClassName, {
            'u-px-8 u-w-48': showMiniBtn,
          })}
          style={{ minWidth: showMiniBtn ? 0 : '10em' }}
          text={
            <>
              <div className={classNames({})}>
                {showMiniBtn ? (
                  <Icon name="bell" className="i-20" />
                ) : (
                  <div>{translate('NotifyMe_Stock')}</div>
                )}
              </div>
            </>
          }></Button>
      </Popover>
    )
  }

  return (
    <Popover
      content={translate('MaxQuantityReached')}
      onToggle={() => setMaximumProductsAdded(false)}
      open={showMaximumProductsAdded}
      className={className}
      theme="light-brown"
      closeBtn={false}
      positions={['top-free']}
      style={{ display: 'grid' }}
      closeOnOutsideClick>
      <Button
        data-test-id="add-to-cart-button"
        aria-label={addToCartButtonText}
        {...props}
        onClick={handleSubmit}
        className={classNames('c-add-to-cart-btn c-btn--block u-mb-0', buttonClassName, {
          'u-px-8 u-w-48': showMiniBtn,
        })}
        disabled={isDisabled}
        primary={primary}
        style={{ minWidth: minWidth && !showMiniBtn ? '10em' : 0 }}
        text={
          <>
            <div
              className={classNames({
                'u-opacity-0': isAddingToCart,
                'd-flex': shoppingCartIcon,
              })}>
              {shoppingCartIcon && (
                <ShoppingCartIcon name="shopping-cart-5" className="i-16 u-mr-8" />
              )}
              {showMiniBtn ? (
                <ShoppingCartIcon name="shopping-cart-5" className="i-20" />
              ) : (
                addToCartButtonText
              )}
            </div>
            {isAddingToCart && (
              <div>
                <Spinner local size={'xxs'}></Spinner>
              </div>
            )}
          </>
        }></Button>
    </Popover>
  )
}

export default AddToCartButton
